import { useEffect } from "react";
import { BrowserRouter, Navigate, Route, Routes, useLocation } from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ConfirmProvider } from "@/components/Confirm";
import { Layout } from "@/components/Layout";
import { ToastProvider } from "@/components/Toast";
import { AdminEvent } from "@/pages/AdminEvent";
import { AgentsGuide } from "@/pages/AgentsGuide";
import { Faq } from "@/pages/Faq";
import { Home } from "@/pages/Home";
import { HowItWorks } from "@/pages/HowItWorks";
import { Imprint } from "@/pages/Imprint";
import { JoinEvent } from "@/pages/JoinEvent";
import { NewEvent } from "@/pages/NewEvent";
import { NotFound } from "@/pages/NotFound";
import { Press } from "@/pages/Press";
import { Privacy } from "@/pages/Privacy";
import { UseCasePage } from "@/pages/UseCasePage";
import { UseCasesIndex } from "@/pages/UseCasesIndex";
import { UserEvent } from "@/pages/UserEvent";
import { VerifyEmail } from "@/pages/VerifyEmail";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      refetchOnWindowFocus: true,
      retry: (failureCount, err) => {
        const status = (err as { status?: number })?.status;
        if (status && status >= 400 && status < 500) return false;
        return failureCount < 2;
      },
    },
  },
});

/**
 * Normalisiert URLs: entfernt genau einen trailing slash (außer bei "/"),
 * damit Links wie `/view/p2foo/` auf `/view/p2foo` greifen und von den
 * Routen sauber gematched werden. React Router v6 unterscheidet sonst
 * zwischen „Segment mit Slash" und „Segment ohne".
 */
function TrailingSlashRedirect() {
  const location = useLocation();
  const { pathname, search, hash } = location;
  useEffect(() => {
    // kein Effekt nötig — Navigate rendert den Redirect.
  }, [pathname]);
  if (pathname.length > 1 && pathname.endsWith("/")) {
    return <Navigate to={pathname.replace(/\/+$/, "") + search + hash} replace />;
  }
  return null;
}

export function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <TrailingSlashRedirect />
        <ToastProvider>
          <ConfirmProvider>
            <Layout>
              <Routes>
                <Route path="/" element={<Home />} />
                <Route path="/new" element={<NewEvent />} />
                <Route path="/view/:shortcut/:userkey" element={<UserEvent />} />
                <Route path="/view/:shortcut" element={<JoinEvent />} />
                <Route path="/verify-email/:shortcut/:token" element={<VerifyEmail />} />
                <Route path="/proadmin/:shortcut/:adminkey" element={<AdminEvent />} />
                <Route path="/how-it-works" element={<HowItWorks />} />
                <Route path="/so-funktionierts" element={<HowItWorks />} />
                <Route path="/faq" element={<Faq />} />
                <Route path="/for-ai-agents" element={<AgentsGuide />} />
                <Route path="/fuer-ki-agenten" element={<AgentsGuide />} />
                <Route path="/privacy" element={<Privacy />} />
                <Route path="/datenschutz" element={<Privacy />} />
                <Route path="/imprint" element={<Imprint />} />
                <Route path="/impressum" element={<Imprint />} />
                <Route path="/press" element={<Press />} />
                <Route path="/presse" element={<Press />} />
                <Route path="/usefor" element={<UseCasesIndex />} />
                <Route path="/usefor/:slug" element={<UseCasePage />} />
                <Route path="*" element={<NotFound />} />
              </Routes>
            </Layout>
          </ConfirmProvider>
        </ToastProvider>
      </BrowserRouter>
    </QueryClientProvider>
  );
}
